import { isItemInCartByTitle } from '@vfs-digital-channels/ns-utils';
import { Field, Form, Formik, FormikErrors, FormikHelpers, FormikTouched } from 'formik';
import React from 'react';
import { Checkbox } from '@vfs-digital-channels/ns-react-components';
import domtoimage from 'dom-to-image';
import { FormLabel, MiniAppFocusWrapper, TapOnPhoneModal } from 'src/components';
import { Environment } from 'src/Environment';
import {
  ApplicationAction,
  ApplicationActionType,
  Catalogue,
  ConsumerType,
  Contact,
  DataStatus,
  FormAddress,
  FormLayout,
  HeadingLayout,
  HeadingOne,
  HeadingTwo,
  HelpButton,
  IdentityTypeDirectors,
  InputField,
  isOnlyDeviceSelected,
  isUserUnderAgeIdNumber,
  ListItem,
  PageLayout,
  ScrollTop,
  SolutionType,
  SOUTH_AFRICA,
  stripWhitespace,
  useTealiumIdle,
  useWizardEffect,
  validateAddressField,
} from 'src/features/common';
import { v4 as uuidv4 } from 'uuid';
import { AddressInput } from 'src/features/common/components/address-input/address-input';
import WebcamChecker from 'src/features/common/components/revamp-webcam-checker/revamp-webcam-checker';
import { notify, Toast } from 'src/features/common/components/toast/toast';
import { UnderAgeModal } from 'src/features/common/components/underage-modal/underage-modal';
import { getApplicationAPIFromApplication } from 'src/features/common/helpers/dto-mappers/salesforce/get-application-api-from-application';
import { useIsMobile } from 'src/features/common/hooks/use-is-mobile';
import { useQueryConfig } from 'src/features/common/hooks/use-query-config';
import { ShoppingCart } from 'src/features/dpo/components';
import { IDENTITY_TYPE_LIST } from 'src/features/onboarding/pages/personal-details/personal-details';
import { FormikChangeListener } from 'src/features/order/components/product-quantity-change-listener/product-quantity-change-listener';
import { QuantityUpdateConfirmModal } from 'src/features/order/components/quantity-update-confirm-modal/quantity-update-confirm-modal';
import { getCatalogueWithoutCategoryChildren } from 'src/features/order/helpers/catalogue-filters';
import { getSolutionsDeliveryBreakdown } from 'src/features/order/helpers/delivery-helpers';
import { useAsyncDispatch } from 'src/features/order/hooks/use-async-dispatch';
import { OtpModal } from 'src/features/order/pages/your-details/components';
import { HelpButtonAction, PageName } from 'src/features/order/services/order-tealium-services';
import { DEVICE_NAMES } from 'src/features/order/tech-debt/product-details-extra';
import { QueryParams } from 'src/features/order/types/query-params';
import {
  asylumMask,
  asylumRegex,
  idMask,
  passportMask,
  passportRegex,
  phoneNumberMaskSpaced,
  refugeeMask,
  refugeeRegex,
  spaceBy4Mask,
} from 'src/helpers/formatting';
import MiniAppEvents from 'src/helpers/miniapp-events';
import {
  isValidEmail,
  isValidID,
  isValidMobileNumber,
  isValidName,
  validateEmailTradeRoot,
} from 'src/helpers/validation';
import { useApplication } from 'src/hooks/useApplication';
import { requestSmsOtp } from 'src/services/otp-service';
import { getProductNamesInCart } from '../../helpers/tealium-service-helpers';
import { requestSignedMerchantAgreement } from 'src/services/merchant-agreement-service';
import { merchantAgreementMiniAppURL, merchantAgreementTerms } from 'src/common';

type ConsumerTypeListItem = {
  value: ConsumerType;
  label: string;
};

const CONSUMER_TYPE_LIST: ConsumerTypeListItem[] = [
  { value: ConsumerType.DIRECTOR, label: 'Business owner/director' },
  { value: ConsumerType.ASSISTANT, label: 'Assistant on behalf of the business owner/director' },
];

function getOTPValidation(): boolean {
  return !!sessionStorage.getItem('verified_mobile_number');
}

function TermsAndConditions({ inMiniApp }: { inMiniApp: boolean }) {
  return (
    <p className="mb-4">
      To continue, please confirm that you have read and accepted the{' '}
      <a
        href={inMiniApp ? merchantAgreementMiniAppURL : merchantAgreementTerms}
        className="text-red-500 underline"
      >
        Merchant agreement
      </a>{' '}
      and understand that rental devices is subject to credit vetting approvals.
    </p>
  );
}

export function YourDetails() {
  const {
    application,
    dispatch,
    salesforceService,
    catalogue,
    dpoTealiumService,
    orderTealiumService,
  } = useApplication();
  const dispatchAsync = useAsyncDispatch(application, dispatch);
  const queryConfig = useQueryConfig<QueryParams>();
  const isMobile = useIsMobile();
  const { initFieldCount, sendIdleEvent } = useTealiumIdle(
    application.tapOnPhoneJourney ? 15_000 : 90_000,
  );
  const [isCartOpen, setIsCartOpen] = React.useState(false);
  const [showTapOnPhoneModal, setShowTapOnPhoneModal] = React.useState(false);
  const [isUnderAge, setUnderAge] = React.useState(false);
  const [otpMobileNumber, setOtpMobileNumber] = React.useState('');
  const [showOtpModal, setShowOtpModal] = React.useState(false);
  const FIRST_NAME_MAX_LENGTH = 40;
  const ref = React.useRef<HTMLDivElement>(null);
  let formTouchedCount = 0;

  const creditVettingRequired = React.useMemo(
    (): boolean => !!application.creditVetting?.required,
    [application.creditVetting],
  );

  const isStoreAgreementEnabled = React.useMemo(
    (): boolean => !Environment.current?.disabledFeatures?.storeAgreement,
    [],
  );

  const isForeignNationalsEnabled = React.useMemo(
    (): boolean => !Environment.current?.disabledFeatures?.passport && !creditVettingRequired,
    [creditVettingRequired],
  );

  const inMiniApp = React.useMemo((): boolean => MiniAppEvents.isInMiniApp(), []);

  const validCart = React.useMemo(
    (): boolean => application.deviceActivationJourney || !!application.cart?.length,
    [application],
  );

  const productNames = getProductNamesInCart(application.cart || []);

  const {
    setNextButtonEnabled,
    setOnNextEventHandler,
    handleNavigateNext,
    setLoadingOverlayVisible,
  } = useWizardEffect(
    () => ({
      nextButtonLabel: application.deviceActivationJourney ? 'Next' : 'Checkout',
      backButtonVisible: true,
      softDisable: false,
      nextButtonEnabled: !isAnyFormErrors(getInitialValues()) && validCart,
      hideProgressBar:
        application.solutionType === SolutionType.buyInStoreFicaOnline &&
        !application.agentJourney &&
        !application.deviceActivationJourney,
    }),
    [application],
  );

  const revampEnabled = React.useMemo(
    (): boolean => !Environment.current.disabledFeatures.revamp,
    [],
  );

  React.useEffect(() => {
    application.tapOnPhoneJourney
      ? orderTealiumService?.onPersonalDetailsToPStart()
      : dpoTealiumService?.onPersonalDetailsLanding(productNames);
  }, []);

  React.useEffect(() => {
    if (catalogue.statusProducts !== DataStatus.LOADED) {
      setLoadingOverlayVisible(true);
      return;
    }

    setLoadingOverlayVisible(false);

    if (
      application.tapOnPhoneJourney &&
      !isItemInCartByTitle(application.cart || [], DEVICE_NAMES.TAP_ON_PHONE)
    ) {
      const tapOnPhoneProduct = catalogue.products.find(
        (product) => product.device.title === DEVICE_NAMES.TAP_ON_PHONE,
      );

      if (!tapOnPhoneProduct || !queryConfig.tap_on_phone_id) {
        throw new Error(
          `Your details revamp: Tap on phone product not found: ${DEVICE_NAMES.TAP_ON_PHONE}`,
        );
      }

      const actions: ApplicationAction[] = [
        {
          type: ApplicationActionType.UPDATE_CART_QUANTITY,
          productId: tapOnPhoneProduct.device.id,
          acquiringChannel: tapOnPhoneProduct.device.acquiringChannel,
          quantity: 1,
          catalogue,
          cartItemId: uuidv4(),
        },
        {
          type: ApplicationActionType.ATTACH_SERIAL_NUMBER,
          productId: tapOnPhoneProduct.device.id,
          serialNumber: [queryConfig.tap_on_phone_id],
        },
      ];

      dispatch({
        type: ApplicationActionType.MULTIPLE_ACTIONS,
        actions,
      });
    }
  }, [application.tapOnPhoneJourney, catalogue.statusProducts]);

  const placeholder =
    ((application.brokerLogin || application.agentJourney) && 'The customer is the...') ||
    'I am the...';

  const getInitialValues = (): FormFields => {
    const address = application?.contact?.businessAddress;

    const consumerType = application?.contact?.consumerType;

    const consumerTypeListItem = CONSUMER_TYPE_LIST.find(
      (listItem) => listItem.value === consumerType,
    );

    const formFields: FormFields = {
      consumerType: consumerTypeListItem,
      idNumber: application?.contact?.idNumber || '',
      identityType: IDENTITY_TYPE_LIST.find(
        (identitType) =>
          identitType.value === (application.contact?.identityType || IdentityTypeDirectors.RSA_ID),
      ),
      name: application?.contact?.name || '',
      surname: application?.contact?.surname || '',
      emailAddress: application?.contact?.emailAddress || '',
      mobileNumber: application?.contact?.mobileNumber || '',
      termsAndConditions: application.termsAndConditions || false,
      address: {
        street: address?.street || '',
        building: address?.building || '',
        suburb: address?.suburb || '',
        city: address?.city || '',
        province: address?.province || '',
        postalCode: address?.postalCode || '',
        country: address?.country || '',
      },
      catalogue: getCatalogueWithoutCategoryChildren(catalogue),
    };

    return formFields;
  };

  const getTotalProducts = (catalogue: Catalogue) =>
    catalogue.products.reduce<number>((currentTotal, product) => {
      return product.device.quantity ? currentTotal + product.device.quantity : currentTotal;
    }, 0);

  const getIdPlaceholder = (identityType: IdentityTypeDirectors | undefined) => {
    switch (identityType) {
      case IdentityTypeDirectors.RSA_ID:
        return 'RSA ID Number';

      case IdentityTypeDirectors.PASSPORT:
        return 'Passport number';

      case IdentityTypeDirectors.REFUGEE:
      case IdentityTypeDirectors.ASYLUM_SEEKER:
      default:
        return 'Document ID';
    }
  };

  const loadImages = (element: HTMLElement): Promise<void[]> => {
    const images = element.getElementsByTagName('img');
    const promises = Array.from(images).map((img) => {
      return new Promise<void>((resolve, reject) => {
        if (img.complete) {
          resolve();
        } else {
          img.onload = () => resolve();
          img.onerror = () => reject(new Error('Image failed to load'));
        }
      });
    });
    return Promise.all(promises);
  };

  const captureAndConvertToBase64 = async () => {
    try {
      if (ref.current) {
        await loadImages(ref.current);
        const dataUrl = await domtoimage.toPng(ref.current, {
          width: ref.current.offsetWidth,
          height: ref.current.offsetHeight,
        });
        dispatch({
          type: ApplicationActionType.SET_MERCHANT_AGREEMENT,
          merchantAgreementScreenshot: dataUrl,
        });

        const response = await requestSignedMerchantAgreement(dataUrl);

        dispatch({
          type: ApplicationActionType.SET_SIGNED_MERCHANT_AGREEMENT,
          signedMerchantAgreement: response.signedAgreementPDF ?? '',
        });

        return response.signedAgreementPDF;
      }
    } catch (error) {
      dispatch({
        type: ApplicationActionType.SET_MERCHANT_AGREEMENT_UPLOADED,
        isMerchantAgreementUploaded: false,
      });
      throw new Error(`Onboarding: Your details: Error capturing image: ${error}`);
    }
  };

  const getIdMask = (values) => {
    switch (values.identityType?.value) {
      case IdentityTypeDirectors.RSA_ID:
        return idMask;
      case IdentityTypeDirectors.PASSPORT:
        return passportMask;
      case IdentityTypeDirectors.ASYLUM_SEEKER:
        return asylumMask;
      case IdentityTypeDirectors.REFUGEE:
        return refugeeMask;
      default:
        return spaceBy4Mask;
    }
  };

  const sendFormStartEvent = (touched: FormikTouched<FormFields>) => {
    if (
      Object.values(touched).some(Boolean) &&
      formTouchedCount == 0 &&
      application.tapOnPhoneJourney
    ) {
      orderTealiumService?.onPersonalDetailsToPFormStart();
      formTouchedCount++;
    }
  };

  const excludedFields = () => {
    return [
      'catalogue',
      'address.building',
      'address.suburb',
      'address.suburbOptions',
      'address.city',
      'address.province',
      'address.postalCode',
      'address.country',
      'identityType',
    ];
  };

  return (
    <section className="[&_.label\-text]:hidden md:[&_.label\-text]:block [&_.label\-text]:text-black">
      <OtpModal
        mobileNumber={otpMobileNumber}
        visible={showOtpModal}
        onClose={() => {
          setShowOtpModal(false);
        }}
        onCancel={() => {
          dpoTealiumService?.onOtpModalAction(productNames, 'cancel');
        }}
        onContinue={() => {
          dpoTealiumService?.onOtpModalAction(productNames, 'continue');
        }}
        onResend={() => {
          dpoTealiumService?.onResendOtp(productNames);
        }}
        onIncorrectOtp={() => {
          dpoTealiumService?.onIncorrectOtp(productNames);
        }}
        onOtpExpired={() => {
          dpoTealiumService?.otpExpired(productNames);
        }}
      />
      {application.deviceActivationJourney && (
        <WebcamChecker
          onShow={() => {
            dpoTealiumService?.onWebcamCheckerModalView(productNames);
          }}
          onClick={() => {}}
        />
      )}

      <PageLayout>
        <ScrollTop />

        {showTapOnPhoneModal && <TapOnPhoneModal onClose={() => setShowTapOnPhoneModal(false)} />}

        <HeadingLayout
          renderMainHeading={() => (
            <HeadingOne>{`Please enter ${
              ((application.brokerLogin || application.agentJourney) && 'the customer') || 'your'
            } details`}</HeadingOne>
          )}
          renderSubHeading={() => <HeadingTwo>Tell us more about yourself</HeadingTwo>}
        />
        {!revampEnabled && (
          <ShoppingCart
            isCartOpen={isCartOpen}
            setIsCartOpen={setIsCartOpen}
            onCheckout={() => handleNavigateNext()}
            application={application}
          />
        )}
        <div ref={ref}>
          <Formik<FormFields>
            innerRef={(formik) =>
              formik &&
              setOnNextEventHandler(() => {
                formik.submitForm();
                dpoTealiumService?.onPersonalDetailsActions(productNames);
              })
            }
            initialValues={getInitialValues()}
            validate={validate}
            onSubmit={submit}
            validateOnChange={true}
            enableReinitialize={true}
          >
            {({ values, setFieldValue, handleBlur, submitForm, touched }) => {
              const setModalIsVisible = (isVisible: boolean) => {
                setFieldValue('isConfirmModalVisible', isVisible);
              };

              sendFormStartEvent(touched);

              return (
                <Form>
                  <UnderAgeModal
                    visible={isUnderAge}
                    onClose={() => {
                      setUnderAge(false);
                      orderTealiumService?.onUnderageModalEnd();
                    }}
                    onExitApplication={() => orderTealiumService?.onUnderageModalAction()}
                  />

                  {initFieldCount<FormFields>(values, excludedFields())}

                  {application.tapOnPhoneJourney
                    ? sendIdleEvent<FormFields>(
                        values,
                        orderTealiumService?.onPersonalDetailsToPIdle,
                        excludedFields(),
                      )
                    : sendIdleEvent<FormFields>(
                        values,
                        (incompleteFields, formCompletionRate) =>
                          dpoTealiumService?.onPersonalDetailsIdle(
                            incompleteFields,
                            formCompletionRate,
                            productNames,
                          ),
                        excludedFields(),
                      )}

                  <FormikChangeListener<FormFields>>
                    {(oldValues, newValues) => {
                      if (oldValues.identityType !== newValues.identityType) {
                        setFieldValue('idNumber', '');
                      }

                      if (oldValues.catalogue.products.length === 0) {
                        return;
                      }

                      newValues.catalogue.products.forEach((product, index) => {
                        let oldValue = oldValues.catalogue.products[index].device.quantity;

                        if (product.device.quantity === oldValue || application.tapOnPhoneJourney) {
                          return;
                        }
                        if (
                          product.device.quantity > oldValue ||
                          (product.device.quantity !== undefined && oldValue === undefined)
                        ) {
                          notify({
                            children: 'Product added to cart',
                            autoClose: 5000,
                          });
                        }
                      });

                      if (
                        getTotalProducts(oldValues.catalogue) !==
                          oldValues.catalogue.maxProductsPerOrder &&
                        getTotalProducts(newValues.catalogue) ===
                          newValues.catalogue.maxProductsPerOrder
                      ) {
                        notify({
                          children: 'Maximum 70 products allowed per order',
                          autoClose: 5000,
                        });
                      }
                    }}
                  </FormikChangeListener>
                  <Toast />
                  <QuantityUpdateConfirmModal
                    isModalVisible={!!values.isConfirmModalVisible}
                    onModalShow={() => orderTealiumService?.onReduceQuantityModalStart()}
                    onCancel={() => {
                      setModalIsVisible(false);
                    }}
                    onConfirm={() => {
                      orderTealiumService?.onReduceQuantityModalAction();
                      submitForm().then(() => {
                        setModalIsVisible(false);
                      });
                    }}
                  />
                  <FormLayout>
                    <Field
                      name="consumerType"
                      type="text"
                      placeholder={placeholder}
                      fieldType="select"
                      items={CONSUMER_TYPE_LIST}
                      getItemDisplayValue={(item) => item.label}
                      component={MiniAppFocusWrapper}
                      innerComponent={InputField}
                      className="light top-field"
                      autoFocus={!isMobile}
                      automationid="Revamp_Your_Details_Consumer_type"
                    />
                    <Field
                      name="name"
                      type="text"
                      placeholder="First name"
                      maxLength={FIRST_NAME_MAX_LENGTH}
                      component={MiniAppFocusWrapper}
                      innerComponent={InputField}
                      fieldType="standard"
                      className="light"
                      automationid="Revamp_Your_Details_First_Name"
                    />
                    <Field
                      name="surname"
                      type="text"
                      placeholder="Surname"
                      component={MiniAppFocusWrapper}
                      innerComponent={InputField}
                      fieldType="standard"
                      className="light"
                      automationid="Revamp_Your_Details_Surname"
                    />
                    <Field
                      name="emailAddress"
                      type="text"
                      placeholder="Email address"
                      handleBlur={handleBlur}
                      component={MiniAppFocusWrapper}
                      innerComponent={InputField}
                      fieldType="standard"
                      className="light"
                      automationid="Revamp_Your_Details_Email_Address"
                    />
                    <Field
                      name="mobileNumber"
                      mask={phoneNumberMaskSpaced}
                      id="mobileNumber"
                      placeholder="Mobile number"
                      className="light"
                      handleBlur={handleBlur}
                      type="tel"
                      fieldType="masked"
                      component={MiniAppFocusWrapper}
                      innerComponent={InputField}
                      automationid="Revamp_Your_Details_Mobile_Number"
                    />

                    {isForeignNationalsEnabled && (
                      <Field
                        name="identityType"
                        type="text"
                        placeholder="ID Type"
                        fieldType="select"
                        items={IDENTITY_TYPE_LIST}
                        getItemDisplayValue={(item) => item.label}
                        component={MiniAppFocusWrapper}
                        innerComponent={InputField}
                        className="light bottom-field"
                        autoFocus={!isMobile}
                      />
                    )}

                    <Field
                      name="idNumber"
                      type={
                        values.identityType?.value === IdentityTypeDirectors.RSA_ID ? 'tel' : 'text'
                      }
                      placeholder={getIdPlaceholder(values.identityType?.value)}
                      component={MiniAppFocusWrapper}
                      innerComponent={InputField}
                      mask={getIdMask(values)}
                      guide={
                        !inMiniApp && values.identityType?.value === IdentityTypeDirectors.RSA_ID
                      }
                      fieldType="masked"
                      className="uppercase bottom-field light"
                      handleBlur={handleBlur}
                      automationid="Revamp_Your_Details_ID_Number"
                    />

                    <FormLabel label="Business address" className="personal-details-label">
                      <Field
                        name="address"
                        component={AddressInput}
                        titleLowerCase="business address"
                        handleBlur={handleBlur}
                        isLightMode={true}
                        onShowBusinessAddress={() => dpoTealiumService?.onShowBusinessAddress()}
                        automationid="Revamp_Your_Details_Business_Address"
                      />
                    </FormLabel>

                    {isStoreAgreementEnabled && (
                      <Field
                        name="termsAndConditions"
                        type="checkbox"
                        component={MiniAppFocusWrapper}
                        innerComponent={Checkbox}
                        label={<TermsAndConditions inMiniApp={inMiniApp} />}
                        variant="secondary"
                        size="md"
                      />
                    )}
                  </FormLayout>
                </Form>
              );
            }}
          </Formik>
        </div>
      </PageLayout>
      <HelpButton
        onOpen={() =>
          orderTealiumService?.onHelpButtonActions(
            PageName.PERSONAL_DETAILS,
            HelpButtonAction.CLICK,
          )
        }
        onEmailClicked={() =>
          orderTealiumService?.onHelpButtonActions(
            PageName.PERSONAL_DETAILS,
            HelpButtonAction.EMAIL,
          )
        }
        onPhoneNumberClicked={() =>
          orderTealiumService?.onHelpButtonActions(PageName.PERSONAL_DETAILS, HelpButtonAction.CALL)
        }
      />
    </section>
  );

  function isAnyFormErrors(values: FormFields) {
    const errors = validate(values);
    return Object.values(errors).length > 0;
  }

  function validate(values: FormFields): FormikErrors<FormFields> {
    const errors: FormikErrors<FormFields> = {};

    if (!values.consumerType) {
      errors.consumerType = 'Please select a valid Consumer Type';
    }

    if (
      values.identityType?.value === IdentityTypeDirectors.RSA_ID &&
      (!values.idNumber || !isValidID(stripWhitespace(values.idNumber)))
    ) {
      errors.idNumber = 'The ID number entered is invalid';
    }

    if (
      values.identityType?.value === IdentityTypeDirectors.RSA_ID &&
      values.idNumber &&
      isValidID(stripWhitespace(values.idNumber)) &&
      isUserUnderAgeIdNumber(values.idNumber)
    ) {
      setUnderAge(true);

      if (isUnderAge) {
        orderTealiumService?.onUnderageModalView();
      }

      errors.idNumber = 'The age limit is 18 years or older';
    }

    if (values.identityType?.value !== IdentityTypeDirectors.RSA_ID && !values.idNumber) {
      errors.idNumber = 'The Identity number entered is invalid';
    }

    if (
      values.identityType?.value === IdentityTypeDirectors.PASSPORT &&
      values.idNumber &&
      !passportRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Passport number entered is invalid';
    }

    if (
      values.identityType?.value === IdentityTypeDirectors.REFUGEE &&
      values.idNumber &&
      !refugeeRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Document ID entered is invalid';
    }

    if (
      values.identityType?.value === IdentityTypeDirectors.ASYLUM_SEEKER &&
      values.idNumber &&
      !asylumRegex.test(values.idNumber)
    ) {
      errors.idNumber = 'The Document ID entered is invalid';
    }

    if (!isValidName(stripWhitespace(values.name))) {
      errors.name = 'The name entered is invalid';
    }

    if (!values.termsAndConditions && isStoreAgreementEnabled) {
      errors.termsAndConditions = 'You must accept the terms and conditions';
    }

    if (!isValidName(stripWhitespace(values.surname))) {
      errors.surname = 'The surname entered is invalid';
    }

    const emailTradeRootValidationError = validateEmailTradeRoot(values.emailAddress);

    if (emailTradeRootValidationError) {
      errors.emailAddress = emailTradeRootValidationError;
    } else if (!isValidEmail(values.emailAddress)) {
      errors.emailAddress = 'The email address entered is invalid';
    }

    if (!isValidMobileNumber(stripWhitespace(values.mobileNumber))) {
      errors.mobileNumber = 'The mobile number entered is invalid';
    }

    if (!values.address) {
      errors.address = 'The address entered is invalid';
    } else {
      const addressError = validateAddressField(values.address);
      if (addressError) {
        // @ts-ignore
        errors.address = addressError;
      }
    }

    setNextButtonEnabled(Object.values(errors).length === 0 && validCart);

    return errors;
  }

  async function submit(values: FormFields, helpers: FormikHelpers<FormFields>) {
    if (!validCart) {
      setNextButtonEnabled(false);
      return;
    }
    // Show OTP modal, if customer hasn't validate their number
    if (application.agentJourney && !getOTPValidation()) {
      setLoadingOverlayVisible(true);
      setOtpMobileNumber(values.mobileNumber || '');
      const { error } = await requestSmsOtp(values.mobileNumber);

      if (error) {
        throw new Error(error);
      }

      setLoadingOverlayVisible(false);
      setShowOtpModal(true);

      dpoTealiumService?.onOtpModalView(productNames);
      return;
    }

    const isOnlyTapOnPhone = isOnlyDeviceSelected(values.catalogue, DEVICE_NAMES.TAP_ON_PHONE);
    // Only true when user has passed in tap_on_phone_id in query params,
    // ie. only when inside HaloDot app or mocking up halodot from browser
    const isTapOnPhoneJourney: boolean = !!application.tapOnPhoneJourney;

    if (
      isOnlyTapOnPhone &&
      !isTapOnPhoneJourney &&
      application.solutionType !== SolutionType.thirdPartyOnboarding
    ) {
      setShowTapOnPhoneModal(true);
      return;
    }

    const { solutionsWithTooManyAddresses } = getSolutionsDeliveryBreakdown(
      application,
      values.catalogue,
      true,
    );

    if (solutionsWithTooManyAddresses.length > 0 && !values.isConfirmModalVisible) {
      helpers.setFieldValue('isConfirmModalVisible', true);
      return;
    }

    setLoadingOverlayVisible(true);
    application.tapOnPhoneJourney && orderTealiumService?.onPersonalDetailsToPEnd();

    const contactDetails: Contact = {
      consumerType: values.consumerType?.value,
      idNumber: values.idNumber?.toUpperCase(),
      identityType: values.identityType?.value,
      name: values.name,
      surname: values.surname,
      emailAddress: values.emailAddress,
      mobileNumber: values.mobileNumber,
      businessAddress: {
        ...values.address,
        country: SOUTH_AFRICA,
      },
    };

    const actions: ApplicationAction[] = [
      {
        type: ApplicationActionType.UPDATE_CONTACT_DETAILS,
        contactDetails: contactDetails,
      },
    ];

    if (values.address) {
      const existingDefaultAddress = (application?.addresses ?? []).find(
        (address) => address.id === 'default',
      );

      // update address if it exists
      if (existingDefaultAddress) {
        actions.push({
          type: ApplicationActionType.UPDATE_ADDRESS,
          address: {
            ...values.address,
            id: existingDefaultAddress.id ?? 'default',
          },
        });
      } else {
        // add new address
        actions.push({
          type: ApplicationActionType.ADD_ADDRESS,
          address: values.address,
        });
      }
    }

    if (isStoreAgreementEnabled && values.termsAndConditions) {
      actions.push({
        type: ApplicationActionType.SET_TERMS_AND_CONDITIONS,
        termsAndConditions: values.termsAndConditions,
      });
    }

    const savedApplicationResponse = await dispatchAsync({
      type: ApplicationActionType.MULTIPLE_ACTIONS,
      actions,
    }).then((applicationNew) => {
      return salesforceService.saveApplication(
        getApplicationAPIFromApplication(applicationNew, catalogue),
        true,
      );
    });

    dispatch({
      type: ApplicationActionType.UPDATE_REFERENCE_NUMBER,
      referenceNumber: savedApplicationResponse?.referenceNumber,
    });

    dpoTealiumService?.personalDetailsSubmitted(
      productNames,
      savedApplicationResponse?.referenceNumber,
    );

    if (isStoreAgreementEnabled && values.termsAndConditions) {
      const signedMerchantAgreement = await captureAndConvertToBase64();

      if (signedMerchantAgreement) {
        await dispatchAsync({
          type: ApplicationActionType.SET_SIGNED_MERCHANT_AGREEMENT,
          signedMerchantAgreement,
        }).then((applicationNew) => {
          return salesforceService
            .saveApplication(getApplicationAPIFromApplication(applicationNew, catalogue), true)
            .then(() => {
              // on success, clear the merchant agreement to save space
              const actions: ApplicationAction[] = [
                {
                  type: ApplicationActionType.SET_MERCHANT_AGREEMENT_UPLOADED,
                  isMerchantAgreementUploaded: true,
                },
                {
                  type: ApplicationActionType.SET_MERCHANT_AGREEMENT,
                  merchantAgreementScreenshot: '',
                },
                {
                  type: ApplicationActionType.SET_SIGNED_MERCHANT_AGREEMENT,
                  signedMerchantAgreement: '',
                },
              ];

              dispatch({
                type: ApplicationActionType.MULTIPLE_ACTIONS,
                actions,
              });
            })
            .catch(() => {
              dispatch({
                type: ApplicationActionType.SET_MERCHANT_AGREEMENT_UPLOADED,
                isMerchantAgreementUploaded: false,
              });
            });
        });
      }
    }

    dispatch({ type: ApplicationActionType.NAVIGATE_TO_NEXT_STEP });
    setLoadingOverlayVisible(false);
  }
}

type FormFields = {
  consumerType?: ConsumerTypeListItem;
  idNumber?: string;
  identityType?: ListItem<IdentityTypeDirectors>;
  name?: string;
  surname?: string;
  emailAddress?: string;
  mobileNumber?: string;
  address?: FormAddress;
  catalogue: Catalogue;
  isConfirmModalVisible?: boolean;
  termsAndConditions?: boolean;
};
